if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        if (this.skipStoreAvailability()) {
          this.clearAvailability();
          return;
        }
        this.fetchAvailability(this.dataset.variantId);
      }

      skipStoreAvailability(sku) {
        const list = (this.dataset.skipStoreAvailabilitySkus || '')
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean);
        const key = String(sku || this.dataset.variantSku || '')
          .trim()
          .toUpperCase();
        return Boolean(key && list.includes(key));
      }

      clearAvailability() {
        this.innerHTML = '';
        this.removeAttribute('available');
      }

      fetchAvailability(variantId) {
        if (!variantId) return;
        if (this.skipStoreAvailability()) {
          this.clearAvailability();
          return;
        }

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            if (this.skipStoreAvailability()) {
              this.clearAvailability();
              return;
            }
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            if (this.skipStoreAvailability()) {
              this.clearAvailability();
              return;
            }
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        if (variant?.sku) this.dataset.variantSku = variant.sku;
        if (variant?.id) this.dataset.variantId = String(variant.id);
        if (this.skipStoreAvailability(variant?.sku)) {
          this.clearAvailability();
          return;
        }
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.clearAvailability();
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML) {
        const existingDrawer = document.querySelector('pickup-availability-drawer');
        // ponytail: never tear down an open drawer — late section fetches were auto-closing on mobile
        const drawerIsOpen = existingDrawer?.hasAttribute('open');

        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          if (!drawerIsOpen) {
            document.querySelector('.pickup-availability-drawer-backdrop')?.remove();
            existingDrawer?.remove();
          }
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');
        const preview = this.querySelector('pickup-availability-preview');
        if (preview) {
          setPickupPreviewLoading(true);
        }

        if (!drawerIsOpen) {
          // Drop any leftover outside-click listener from a replaced drawer instance
          if (existingDrawer && typeof existingDrawer.teardownOutsideClick === 'function') {
            existingDrawer.teardownOutsideClick();
          }
          document.querySelector('.pickup-availability-drawer-backdrop')?.remove();
          existingDrawer?.remove();
          document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
          const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
          colorClassesToApply.forEach((colorClass) => {
            document.querySelector('pickup-availability-drawer').classList.add(colorClass);
          });
        }

        const button = this.querySelector('#ShowPickupAvailabilityDrawer');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
        syncPickupPreviewAfterRender();
      }
    }
  );
}

document.addEventListener('click', (evt) => {
  const opener = evt.target.closest('[data-d1-open-store-availability]');
  if (!opener || opener.getAttribute('aria-disabled') === 'true') return;

  evt.preventDefault();
  evt.stopPropagation();

  const focusAfterClose =
    document.querySelector('#ShowPickupAvailabilityDrawer') ||
    opener.querySelector('button.product__inventory') ||
    opener;

  const openDrawer = () => {
    // Re-query: late section fetches can replace the node while stock prefetch runs
    const drawer = document.querySelector('pickup-availability-drawer');
    if (!drawer || typeof drawer.show !== 'function') return Promise.resolve();
    return Promise.resolve(drawer.show(focusAfterClose));
  };

  opener.setAttribute('aria-busy', 'true');
  openDrawer()
    .catch(() => {})
    .finally(() => {
      opener.removeAttribute('aria-busy');
    });
});

function stockRankForCard(card) {
  if (card.classList.contains('pickup-availability-card--available')) return 0;
  if (card.classList.contains('pickup-availability-card--low')) return 1;
  if (card.classList.contains('pickup-availability-card--contact')) return 2;
  return 3;
}

function updatePickupNearCaption(drawer, postcode) {
  const near = drawer.querySelector('[data-d1-pickup-near]');
  if (!near) return;
  if (!postcode) {
    near.hidden = true;
    near.textContent = '';
    return;
  }
  const template = near.getAttribute('data-d1-pickup-near-template') || 'Nearest to __PC__';
  near.textContent = template.split('__PC__').join(postcode);
  near.hidden = false;
}

function distanceKmForCard(card) {
  const raw = card.getAttribute('data-distance-km');
  if (raw == null || raw === '') return null;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function applyPickupStoreSort(drawer, postcode) {
  const list = drawer?.querySelector('[data-store-availability-drawer-content]');
  if (!list) return;
  const proximity = window.D1PickupProximity;
  const cards = Array.prototype.slice.call(list.querySelectorAll('.pickup-availability-card'));
  cards.sort((a, b) => {
    if (!proximity?.compareStores) {
      return stockRankForCard(a) - stockRankForCard(b);
    }
    return proximity.compareStores(
      {
        postcode: a.getAttribute('data-postcode') || '',
        state: a.getAttribute('data-d1-state') || '',
        stockRank: stockRankForCard(a),
        distanceKm: distanceKmForCard(a),
      },
      {
        postcode: b.getAttribute('data-postcode') || '',
        state: b.getAttribute('data-d1-state') || '',
        stockRank: stockRankForCard(b),
        distanceKm: distanceKmForCard(b),
      },
      postcode
    );
  });
  cards.forEach((card) => list.appendChild(card));
}

function isBrunswickPickupCard(card) {
  const title = card && card.querySelector('.pickup-availability-card__title');
  return /brunswick/i.test((title && title.textContent) || '');
}

function pickupCardAvailable(card) {
  return (
    card.classList.contains('pickup-availability-card--available') ||
    card.classList.contains('pickup-availability-card--low')
  );
}

function featuredPickupCard(drawer, postcode) {
  const cards = Array.prototype.slice.call(drawer?.querySelectorAll('.pickup-availability-card') || []);
  if (!cards.length) return null;
  if (!postcode) return cards.find(isBrunswickPickupCard) || cards[0];

  const proximity = window.D1PickupProximity;
  const ranked = cards.slice().sort((a, b) => {
    if (!proximity?.compareStores) return 0;
    return proximity.compareStores(
      {
        postcode: a.getAttribute('data-postcode') || '',
        state: a.getAttribute('data-d1-state') || '',
        stockRank: 0,
        distanceKm: distanceKmForCard(a),
      },
      {
        postcode: b.getAttribute('data-postcode') || '',
        state: b.getAttribute('data-d1-state') || '',
        stockRank: 0,
        distanceKm: distanceKmForCard(b),
      },
      postcode
    );
  });
  return ranked[0];
}

function pickupPreviewStatus(available, name) {
  const store = String(name || 'store').trim() || 'store';
  return available ? 'Currently Available at ' + store : 'Currently Unavailable at ' + store;
}

function pickupStockReady(drawer) {
  return drawer?.getAttribute('data-d1-stock-applied') === 'true';
}

function setPickupPreviewLoading(on) {
  const preview = document.querySelector('pickup-availability-preview');
  if (!preview) return;
  preview.classList.toggle('is-loading', on);
  preview.setAttribute('aria-busy', on ? 'true' : 'false');
  if (!on) return;
  preview.classList.remove('is-available', 'is-unavailable');
  const titleEl = preview.querySelector('.caption-large');
  if (titleEl) titleEl.textContent = '';
  const ready = preview.querySelector('.pickup-availability-preview__ready');
  if (ready) ready.hidden = true;
}

function paintPickupPreview(card) {
  const preview = document.querySelector('pickup-availability-preview');
  if (!preview || !card) return;
  const name = (card.querySelector('.pickup-availability-card__title')?.textContent || '').trim() || 'store';
  const available = pickupCardAvailable(card);
  const titleEl = preview.querySelector('.caption-large');
  if (titleEl) {
    titleEl.textContent = pickupPreviewStatus(available, name);
  }
  const ready = preview.querySelector('.pickup-availability-preview__ready');
  if (ready) {
    ready.hidden = !available;
  }
  preview.classList.toggle('is-available', available);
  preview.classList.toggle('is-unavailable', !available);
}

function refreshPickupPreview(drawer) {
  const host = drawer || document.querySelector('pickup-availability-drawer');
  if (!host) return;
  if (!pickupStockReady(host)) {
    setPickupPreviewLoading(true);
    return;
  }
  setPickupPreviewLoading(false);
  const postcode = window.D1PickupProximity?.readUserPostcode?.() || '';
  paintPickupPreview(featuredPickupCard(host, postcode));
}

function whenStockApiReady() {
  const api = window.D1BundleStoreAvailability;
  if (api && typeof api.prepareDrawer === 'function') return Promise.resolve(api);
  return new Promise((resolve) => {
    let n = 0;
    const tick = () => {
      const later = window.D1BundleStoreAvailability;
      if (later && typeof later.prepareDrawer === 'function') {
        resolve(later);
        return;
      }
      if (n++ > 20) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

function syncPickupPreviewAfterRender() {
  const drawer = document.querySelector('pickup-availability-drawer');
  setPickupPreviewLoading(true);
  const finish = () => sortPickupStoreCards(drawer);
  whenStockApiReady().then((api) => {
    if (api) return Promise.resolve(api.prepareDrawer()).catch(() => {});
    if (drawer && !pickupStockReady(drawer)) drawer.setAttribute('data-d1-stock-applied', 'true');
  }).then(finish);
}

function sortPickupStoreCards(drawer) {
  if (!drawer) return;
  const proximity = window.D1PickupProximity;
  const postcode = proximity?.readUserPostcode?.() || '';
  applyPickupStoreSort(drawer, postcode);
  updatePickupNearCaption(drawer, postcode);
  refreshPickupPreview(drawer);
  if (!postcode || !proximity?.enrichStoreDistances) return;
  proximity.enrichStoreDistances(drawer, postcode).then((painted) => {
    if (!painted) return;
    applyPickupStoreSort(drawer, postcode);
    refreshPickupPreview(drawer);
  });
}

window.sortPickupStoreCards = sortPickupStoreCards;

function applyPickupStoreFilter(drawer, filterKey) {
  const list = drawer.querySelector('[data-store-availability-drawer-content]');
  const empty = drawer.querySelector('[data-d1-pickup-filter-empty]');
  if (!list) return;

  const filter = (filterKey || 'ALL').toUpperCase();
  const items = list.querySelectorAll('.pickup-availability-card[data-d1-state]');
  let visibleCount = 0;

  items.forEach((li) => {
    const state = (li.dataset.d1State || '').toUpperCase();
    let show = true;

    if (filter === 'ALL') {
      show = true;
    } else if (filter === 'VIC') {
      show = state === 'VIC';
    } else {
      show = state === filter;
    }

    li.hidden = !show;
    if (show) visibleCount += 1;
  });

  if (empty) {
    empty.hidden = visibleCount > 0;
  }

  drawer.querySelectorAll('.pickup-availability-filters [data-d1-filter]').forEach((btn) => {
    const active = (btn.dataset.d1Filter || '').toUpperCase() === filter;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
}

window.applyPickupStoreFilter = applyPickupStoreFilter;

document.addEventListener('click', (e) => {
  const filterBtn = e.target.closest('.pickup-availability-filters [data-d1-filter]');
  if (!filterBtn) return;
  const drawer = filterBtn.closest('pickup-availability-drawer');
  if (!drawer) return;
  e.preventDefault();
  applyPickupStoreFilter(drawer, filterBtn.dataset.d1Filter);
});

document.addEventListener('d1:postcode-changed', () => {
  const drawer = document.querySelector('pickup-availability-drawer');
  if (!drawer) return;
  const postcode = window.D1PickupProximity?.readUserPostcode?.() || '';
  sortPickupStoreCards(drawer);
  if (drawer.hasAttribute('open')) {
    applyPickupStoreFilter(drawer, window.D1PickupProximity?.filterKeyForPostcode?.(postcode) || 'ALL');
  }
});

window.D1PickupPreview = {
  isBrunswickPickupCard,
  featuredPickupCard,
  pickupPreviewStatus,
  pickupStockReady,
  setPickupPreviewLoading,
  paintPickupPreview,
  refreshPickupPreview,
};

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);
        this.backdropEl = null;
        this._openedAt = 0;

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      ensureBackdrop() {
        if (!this.backdropEl) {
          this.backdropEl = document.createElement('div');
          this.backdropEl.className = 'pickup-availability-drawer-backdrop';
          this.backdropEl.setAttribute('aria-hidden', 'true');
          this.backdropEl.addEventListener('click', () => this.hide());
        }
        if (!this.backdropEl.isConnected) {
          document.body.insertBefore(this.backdropEl, this);
        }
        this.backdropEl.classList.add('pickup-availability-drawer-backdrop--visible');
      }

      teardownOutsideClick() {
        document.body.removeEventListener('click', this.onBodyClick);
      }

      handleBodyClick(evt) {
        // Ignore the opening gesture / synthetic leftovers (rAF alone is not enough on some mobile browsers)
        if (Date.now() - (this._openedAt || 0) < 500) return;

        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          !target.closest('#ShowPickupAvailabilityDrawer') &&
          !target.closest('[data-d1-open-store-availability]')
        ) {
          this.hide();
        }
      }

      lockPageScroll() {
        if (this._pageLocked) return;
        this._pageLocked = true;
        this._lockY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        // ponytail: iOS still scrolls the page behind overflow:hidden; pin it.
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._lockY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }

      unlockPageScroll() {
        if (!this._pageLocked) return;
        this._pageLocked = false;
        document.documentElement.classList.remove('overflow-hidden');
        document.body.classList.remove('overflow-hidden');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, this._lockY || 0);
      }

      hide() {
        this.removeAttribute('open');
        this.backdropEl?.classList.remove('pickup-availability-drawer-backdrop--visible');
        this.teardownOutsideClick();
        this.unlockPageScroll();
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;

        const finishOpen = () => {
          // Drawer node may have been replaced while stock prefetch ran
          const live = document.querySelector('pickup-availability-drawer');
          if (live && live !== this && typeof live.show === 'function') {
            return live.show(focusElement);
          }

          this._openedAt = Date.now();
          this.ensureBackdrop();
          this.setAttribute('open', '');
          this.lockPageScroll();
          const postcode = window.D1PickupProximity?.readUserPostcode?.() || '';
          const filter = window.D1PickupProximity?.filterKeyForPostcode?.(postcode) || 'ALL';
          sortPickupStoreCards(this);
          applyPickupStoreFilter(this, filter);
          trapFocus(this);
          requestAnimationFrame(() => {
            if (!this.hasAttribute('open')) return;
            document.body.addEventListener('click', this.onBodyClick);
          });
        };

        // Always overlay honest d1x status before paint — Shopify continue-selling lies
        if (window.D1BundleStoreAvailability?.prepareDrawer) {
          return Promise.resolve(window.D1BundleStoreAvailability.prepareDrawer())
            .catch(() => {})
            .then(finishOpen);
        }

        finishOpen();
        return Promise.resolve();
      }
    }
  );
}
